import { addDays, format, startOfDay } from 'date-fns';
import type { ComputedShift } from '@/hooks/useShifts';
import type { CalendarServiceItem } from '@/types';

export const UTILIZATION_ROW_ID = 'calendar-utilization';
export const UTILIZATION_ROW_HEIGHT = 56;
const HOUR = 3_600_000;

/** Why a day has no utilization percentage — not a change to the ratio itself. */
export type UtilizationCapacityStatus = 'ready' | 'none' | 'unknown';

/**
 * Period-wide reason the calendar has no capacity to show (#2333).
 * `no_mechanics` is distinct from `empty_filter`: an empty workshop must not
 * be described as a cleared mechanic chip list.
 */
export type CalendarCapacityGap = 'no_mechanics' | 'empty_filter' | 'no_shifts';

export interface DailyUtilization {
  date: string;
  /** Shift hours of the selected mechanics, lunch deducted. */
  availableHours: number;
  /** Hours of work orders assigned to those same mechanics. */
  occupiedHours: number;
  /** Work orders with no mechanic — shown separately, never in the percentage. */
  unassignedHours: number;
  percent: number | null;
  /**
   * Distinguishes `percent: null` causes without changing the ratio.
   * `unknown` = no mechanics selected (no information);
   * `none` = selected mechanics have no shift hours (no capacity);
   * `ready` = shift hours exist and `percent` is the ratio.
   */
  capacityStatus?: UtilizationCapacityStatus;
}

/** Split elapsed time at local midnight, preserving net hours across days. */
function splitHours(start: number, end: number, hours: number, consume: (date: string, hours: number) => void) {
  if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start || !Number.isFinite(hours) || hours <= 0) return;
  for (let cursor = start; cursor < end;) {
    const next = Math.min(end, addDays(startOfDay(new Date(cursor)), 1).getTime());
    consume(format(new Date(cursor), 'yyyy-MM-dd'), hours * (next - cursor) / (end - start));
    cursor = next;
  }
}

/**
 * Daily utilization = the selected mechanics' work orders ÷ their shift hours.
 *
 * Both sides describe the same people (#2261). Absence is not handled here:
 * a mechanic on sick leave or vacation gets no shift from the API at all, so
 * their capacity is already gone — and work still assigned to them on such a
 * day is real overbooking that belongs in the numerator.
 *
 * Recorded work time is deliberately absent: a work order that never gets
 * closed keeps accruing hours long after it left the calendar, which made the
 * day read several hundred percent while its rows looked empty.
 */
export function buildDailyUtilization({
  start, end, mechanicIds, shifts, items, unpaidLunchMinutes,
}: {
  start: Date;
  end: Date;
  mechanicIds: string[];
  shifts: ComputedShift[];
  items: CalendarServiceItem[];
  unpaidLunchMinutes: number;
}): DailyUtilization[] {
  const selected = new Set(mechanicIds);
  const days = new Map<string, DailyUtilization>();
  for (let date = startOfDay(start); date < end; date = addDays(date, 1)) {
    const key = format(date, 'yyyy-MM-dd');
    days.set(key, {
      date: key, availableHours: 0, occupiedHours: 0, unassignedHours: 0,
      percent: null, capacityStatus: selected.size === 0 ? 'unknown' : 'none',
    });
  }

  // Union overlapping shifts per mechanic, then deduct lunch once per day.
  const windows = new Map<string, Map<string, [number, number][]>>();
  for (const shift of shifts) {
    if (!selected.has(shift.userId)) continue;
    const from = new Date(`${shift.date}T${shift.startTime}`).getTime();
    let to = new Date(`${shift.date}T${shift.endTime}`).getTime();
    if (to < from) to = addDays(new Date(to), 1).getTime();
    splitHours(from, to, (to - from) / HOUR, (date) => {
      if (!days.has(date)) return;
      const users = windows.get(date) ?? new Map<string, [number, number][]>();
      const spans = users.get(shift.userId) ?? [];
      const midnight = new Date(`${date}T00:00:00`);
      spans.push([Math.max(from, midnight.getTime()), Math.min(to, addDays(midnight, 1).getTime())]);
      users.set(shift.userId, spans);
      windows.set(date, users);
    });
  }
  for (const [date, users] of windows) {
    for (const spans of users.values()) {
      spans.sort((a, b) => a[0] - b[0]);
      let total = 0;
      let previousEnd = -Infinity;
      for (const [from, to] of spans) {
        total += Math.max(0, to - Math.max(from, previousEnd));
        previousEnd = Math.max(previousEnd, to);
      }
      const lunch = Math.max(0, unpaidLunchMinutes) * 60_000;
      days.get(date)!.availableHours += (total > lunch ? total - lunch : total) / HOUR;
    }
  }

  // Work that never becomes shop-floor hours is not load.
  const excluded = new Set(['cancelled', 'quote_rejected', 'quote']);
  for (const item of items) {
    if (item.isTentative || excluded.has(item.bookingStatus)) continue;
    const assigned = item.assignedMechanicId;
    if (assigned !== null && !selected.has(assigned)) continue;
    const field = assigned === null ? 'unassignedHours' : 'occupiedHours';
    splitHours(Date.parse(item.scheduledStart), Date.parse(item.scheduledEnd), item.durationHours, (date, hours) => {
      const day = days.get(date);
      if (day) day[field] += hours;
    });
  }

  for (const day of days.values()) {
    day.percent = day.availableHours > 0 ? Math.round(day.occupiedHours / day.availableHours * 100) : null;
    day.capacityStatus = selected.size === 0 ? 'unknown' : day.availableHours > 0 ? 'ready' : 'none';
  }
  return [...days.values()];
}

/**
 * Period-level empty-capacity reason. A single empty day in a week that has
 * shift hours is not a gap — only the whole visible window being empty is.
 *
 * Three causes, not two (#2333): an empty workshop is not an empty filter.
 * Empty selection wins over missing shifts when mechanics exist, because the
 * user has a different fix (restore the filter vs record shifts).
 */
export function describeCalendarCapacityGap(
  days: DailyUtilization[],
  {
    selectedMechanicIds,
    availableMechanicCount,
  }: {
    selectedMechanicIds: readonly string[];
    availableMechanicCount: number;
  },
): CalendarCapacityGap | null {
  if (selectedMechanicIds.length === 0) {
    return availableMechanicCount === 0 ? 'no_mechanics' : 'empty_filter';
  }
  if (days.length === 0) return null;
  if (days.every((day) => !(day.availableHours > 0))) return 'no_shifts';
  return null;
}
